import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useGoldPrice } from '@/hooks/useGoldPrice';
import { useTradingStatus } from '@/hooks/useTradingStatus';

function MiniTrend({ data, isUp }: { data: number[]; isUp: boolean }) {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 48;
  const h = 20;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * h * 0.8 - h * 0.1;
      return `${x},${y}`;
    })
    .join(' ');
  const color = isUp ? '#ef4444' : '#22c55e';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
    </svg>
  );
}

export default function MarketSection() {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const { getPrice, getChange, formatPrice, getSparkline, getDirection, isLive } = useGoldPrice();
  const { status: tradingStatus } = useTradingStatus();
  // 休市时不显示「行情未连接」（行情源本就不推送，属于休市而非断连）
  const marketClosed = !!tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen;

  const items = [
    { key: '黄金', label: 'Au9999', sub: '现货黄金（元/克）', color: '#d4a843' },
    { key: '白银', label: 'Ag9999', sub: '现货白银（元/克）', color: '#b08d70' },
  ];

  const [flashMap, setFlashMap] = useState<Record<string, 'up' | 'down' | null>>({});
  const prevPriceMap = useRef<Record<string, number>>({});

  useEffect(() => {
    items.forEach((item) => {
      const price = getPrice(item.key);
      const prev = prevPriceMap.current[item.key] || 0;
      if (price > 0 && prev > 0 && price !== prev) {
        const dir = getDirection(item.key);
        if (dir !== 'flat') {
          setFlashMap((prevMap) => ({ ...prevMap, [item.key]: dir }));
          setTimeout(() => {
            setFlashMap((prevMap) => ({ ...prevMap, [item.key]: null }));
          }, 800);
        }
      }
      prevPriceMap.current[item.key] = price;
    });
  }, [items, getPrice, getDirection]);

  return (
    <div className="px-3 mt-5">
      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-brown-900 text-base font-bold">
          {t('marketRefTitle')}
        </h3>
        <button
          onClick={() => navigate('/market')}
          className="flex items-center gap-1 text-brown-500 text-xs cursor-pointer hover:text-brown-700 transition-colors whitespace-nowrap"
        >
          <span>{t('moreMarket')}</span>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm"></i>
          </span>
        </button>
      </div>

      {/* 行情列表 */}
      <div className="space-y-2">
        {items.map((item) => {
          const price = getPrice(item.key);
          const change = getChange(item.key);
          const sign = change >= 0 ? '+' : '';
          const isUp = change >= 0;
          const sparkData = getSparkline(item.key, 12);
          const flash = flashMap[item.key];
          const flashClass = flash === 'up' ? 'animate-flash-up' : flash === 'down' ? 'animate-flash-down' : '';

          return (
            <div
              key={item.key}
              onClick={() => navigate('/market')}
              className="flex items-center justify-between bg-white rounded-xl px-4 py-3.5 border border-gold-50 cursor-pointer"
            >
              {/* 左侧：品种信息 */}
              <div className="flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div>
                  <p className="text-gold-600 text-sm font-semibold">
                    {item.label}
                  </p>
                  <p className="text-brown-400 text-[10px] mt-0.5">{item.sub}</p>
                </div>
              </div>

              {/* 右侧：价格+涨幅+迷你趋势 */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-brown-900 text-lg font-bold ${flashClass}`}>
                    ¥{price > 0 ? formatPrice(item.key, item.key === '黄金' ? 2 : 3) : '---'}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <span className={`text-xs font-medium ${isUp ? 'text-red-500' : 'text-green-500'}`}>
                      {price > 0 ? `${sign}${change.toFixed(2)}%` : '+0.00%'}
                    </span>
                    {!isLive && !marketClosed && (
                      <span className="text-red-500 text-[10px] bg-red-50 px-1 rounded">行情未连接</span>
                    )}
                  </div>
                </div>
                <MiniTrend data={sparkData} isUp={isUp} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}